// Spec PWA-019 D-02/D-05 — calendario da cobranca mensal e situacao do apadrinhamento feito pelo app. Funcoes puras, usadas
// pelo servidor (rotina de cobranca, rotas) e pela tela ("a partir de" ao mudar valor ou dia) e pelo sandbox.
// Datas sao 'AAAA-MM-DD' no fuso de Brasilia (UTC-3 fixo, sem horario de verao desde 2019); meses sao 'AAAA-MM'.

export const BRAZIL_OFFSET_MS = 3 * 60 * 60 * 1000
/** Cartao "vencendo": a validade acaba antes da proxima cobranca ou ate estes dias a partir de hoje. */
export const CARD_EXPIRING_DAYS = 30
/** Novas tentativas depois da 1a recusa (dias); esgotadas, a cobranca fica em aberto ate trocar o cartao. */
export const CHARGE_RETRY_DAYS = [3, 6] as const

export const APP_SPONSORSHIP_STATUSES = ['ok', 'payment_issue', 'card_expiring', 'card_expired', 'canceled'] as const
export type AppSponsorshipStatus = (typeof APP_SPONSORSHIP_STATUSES)[number]

export function todayInBrazil(now: Date): string {
    return new Date(now.getTime() - BRAZIL_OFFSET_MS).toISOString().slice(0, 10)
}

export const monthOf = (date: string): string => date.slice(0, 7)

export function addMonths(month: string, n: number): string {
    const [y, m] = month.split('-').map(Number) as [number, number]
    const total = y * 12 + (m - 1) + n
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

/** Dia de vencimento no mes (os dias permitidos, 5 a 25, existem em todos os meses). */
export const dueOn = (month: string, dueDay: number): string => `${month}-${String(dueDay).padStart(2, '0')}`

export type BillingPlan = {
    /** Primeiro dia que pode ter vencimento cobrado (dia seguinte ao apadrinhamento, ou a subida da PWA-019). */
    billingStartsOn: string
    dueDay: number
    /** Meses de referencia que ja tem cobranca (paga, recusada, em nova tentativa ou descartada). */
    charged: ReadonlySet<string>
}

/** Meses devidos e ainda sem cobranca, do mais antigo ao mais novo (normalmente zero ou um). Trocar o dia so afeta
    meses sem cobranca: nunca duas no mesmo mes, e o mes cujo dia novo ja passou continua devido (C#2). */
export function dueMonths(plan: BillingPlan, today: string): string[] {
    const out: string[] = []
    for (let month = monthOf(plan.billingStartsOn); month <= monthOf(today); month = addMonths(month, 1)) {
        const on = dueOn(month, plan.dueDay)
        if (on >= plan.billingStartsOn && on <= today && !plan.charged.has(month)) out.push(month)
    }
    return out
}

/** Proxima cobranca ainda nao feita: hoje, se ha mes devido sem cobranca; senao o proximo vencimento. */
export function nextCharge(plan: BillingPlan, today: string): { month: string; on: string } {
    const due = dueMonths(plan, today)
    if (due.length) return { month: due[0]!, on: today }
    const from = today > plan.billingStartsOn ? today : plan.billingStartsOn
    for (let month = monthOf(from); ; month = addMonths(month, 1)) {
        const on = dueOn(month, plan.dueDay)
        if (on >= from && !plan.charged.has(month)) return { month, on }
    }
}

/** Ultimo dia de validade do cartao ('MM/AAAA'). */
export function cardLastDay(expiry: string): string {
    const [mm, yyyy] = expiry.split('/') as [string, string]
    const last = new Date(Date.UTC(Number(yyyy), Number(mm), 0))
    return last.toISOString().slice(0, 10)
}

export function addDays(date: string, days: number): string {
    return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
}

/** D-05: problema no pagamento e cartao vencido tem prioridade; cancelado e cancelado. */
export function sponsorshipStatusOf(input: {
    canceled: boolean
    hasOpenCharge: boolean
    cardExpiry: string
    nextChargeOn: string | null
    today: string
}): AppSponsorshipStatus {
    if (input.canceled) return 'canceled'
    if (input.hasOpenCharge) return 'payment_issue'
    const last = cardLastDay(input.cardExpiry)
    if (last < input.today) return 'card_expired'
    if (last <= addDays(input.today, CARD_EXPIRING_DAYS) || (input.nextChargeOn !== null && last < input.nextChargeOn)) return 'card_expiring'
    return 'ok'
}
